import { styled, Box, Typography, TextField } from "@mui/material";
import { dumontColors } from "../../colors";

export const Header = styled(Box)({
  height: 65,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 20px",
  borderBottom: `1px solid ${dumontColors.border}`,
  flexShrink: 0,
});

export const LogoContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
});

export const Title = styled(Typography)({
  fontSize: 16,
  fontWeight: 600,
  color: dumontColors.text,
  fontFamily: "Manrope",
});

export const CloseButton = styled(Box)({
  width: 32,
  height: 32,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  borderRadius: "6px",
  "&:hover": {
    backgroundColor: dumontColors.gray100,
  },
});

export const SearchBarContainer = styled(Box)({
  height: 62.5,
  padding: "12px 12px 1px 12px",
  borderBottom: `1px solid ${dumontColors.border}`,
  position: "relative",
  flexShrink: 0,
});

export const SearchInputWrapper = styled(Box)({
  height: 37.5,
  backgroundColor: dumontColors.white,
  border: `1px solid ${dumontColors.border}`,
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  padding: "0 12px 0 36px",
  position: "relative",
});

export const SearchIconContainer = styled(Box)({
  position: "absolute",
  left: 12,
  top: "50%",
  transform: "translateY(-50%)",
  pointerEvents: "none",
  zIndex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

export const StyledSearchField = styled(TextField)({
  "& .MuiInputBase-root": {
    fontSize: 13,
    fontWeight: 400,
    color: dumontColors.gray400,
    fontFamily: "Manrope",
    lineHeight: 1.366,
    "&::before": { display: "none" },
    "&::after": { display: "none" },
  },
  "& .MuiInputBase-input": {
    padding: 0,
    color: dumontColors.textSecondary,
    "&::placeholder": {
      color: dumontColors.gray400,
      opacity: 1,
    },
  },
});

export const ScrollableContent = styled(Box)({
  flex: 1,
  overflowY: "auto",
  padding: "12px",
});

export const NavigationSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

export const SectionLabel = styled(Box)({
  padding: "8px 12px",
});

export const SectionTitle = styled(Typography)({
  fontSize: 11,
  fontWeight: 500,
  color: dumontColors.textTertiary,
  fontFamily: "Manrope",
  textTransform: "uppercase",
  letterSpacing: "0.025em",
});

export const NavItem = styled(Box)<{ isActive: boolean }>(({ isActive }) => ({
  height: 40,
  backgroundColor: isActive ? dumontColors.primary : "transparent",
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "0 0 0 12px",
  cursor: "pointer",
  boxShadow: isActive ? `0px 2px 4px 0px ${dumontColors.shadow}` : "none",
  "&:hover": {
    backgroundColor: isActive ? dumontColors.primary : dumontColors.hoverBackground,
  },
}));

export const NavItemText = styled(Typography)<{ isActive: boolean }>(({ isActive }) => ({
  fontSize: 14,
  fontWeight: 500,
  color: isActive ? dumontColors.white : dumontColors.textTertiary,
  fontFamily: "Manrope",
}));

export const SectionContainer = styled(Box)({
  marginTop: "12px",
});

export const SectionHeader = styled(Box)<{ isActive: boolean }>(({ isActive }) => ({
  height: 40,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 12px",
  borderRadius: "8px",
  cursor: "pointer",
  backgroundColor: isActive ? dumontColors.primary : "transparent",
  "&:hover": {
    backgroundColor: isActive ? dumontColors.primary : dumontColors.hoverBackground,
  },
}));

export const SectionHeaderContent = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
});

export const SectionHeaderText = styled(Typography)<{ isActive: boolean }>(({ isActive }) => ({
  fontSize: 14,
  fontWeight: 500,
  color: isActive ? dumontColors.white : dumontColors.textTertiary,
  fontFamily: "Manrope",
}));

export const ItemsList = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  marginTop: "4px",
  paddingLeft: "12px",
});

export const EmptyState = styled(Box)({
  padding: "8px 0 8px 12px",
});

export const EmptyStateText = styled(Typography)({
  fontSize: 13,
  fontWeight: 400,
  color: "#98A2B3",
  fontFamily: "Manrope",
});

export const ChatItem = styled(Box)({
  padding: "8px 0 8px 12px",
  borderRadius: "6px",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: dumontColors.hoverBackground,
  },
});

export const ChatTitle = styled(Typography)<{ isActive?: boolean }>(({ isActive }) => ({
  fontSize: 13,
  fontWeight: 400,
  color: isActive ? dumontColors.primary : dumontColors.textTertiary,
  fontFamily: "Manrope",
  marginBottom: "4px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  lineHeight: 1.4,
}));

export const ChatTime = styled(Typography)({
  fontSize: 11,
  fontWeight: 400,
  color: "#98A2B3",
  fontFamily: "Manrope",
  lineHeight: 1.4,
});

export const AgentItem = styled(Box)({
  height: 34.19,
  display: "flex",
  alignItems: "center",
  padding: "0 12px",
  borderRadius: "6px",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: dumontColors.hoverBackground,
  },
});

export const AgentName = styled(Typography)<{ isActive?: boolean }>(({ isActive }) => ({
  fontSize: 13,
  fontWeight: 400,
  color: isActive ? dumontColors.primary : dumontColors.textTertiary,
  fontFamily: "Manrope",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  lineHeight: 1.4,
}));

export const Footer = styled(Box)({
  padding: "24px 12px",
  borderTop: "1px solid #EBEBEB",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  flexShrink: 0,
});

export const FooterButton = styled(Box)({
  height: 40,
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "0 0 0 12px",
  borderRadius: "8px",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: dumontColors.hoverBackground,
  },
});

export const FooterButtonText = styled(Typography)<{ isPrimary?: boolean }>(({ isPrimary }) => ({
  fontSize: 14,
  fontWeight: 500,
  color: isPrimary ? dumontColors.primary : dumontColors.textTertiary,
  fontFamily: "Manrope",
}));
